import { useCallback, useEffect, useState } from "react";
import { getCurrencyFromBrowserLocale, type SupportedCurrency } from "~/lib/use-currency-preference";
import type { SupportedLocale } from "~/lib/use-locale-preference";
import { getCart, useEmbeddedCartReady, type ProductSelection } from "~/lib/use-embedded-cart-global";

// A brand-new cart cookie has no `currency` field, so Cart.js's pricing fetch runs with
// `currency=undefined` and returns nothing — "empty cart" despite a real quantity.
// Seed a browser-locale default once ready. Mount this once near the app root.
export function useInitializeEmbeddedCartCurrency(): void {
  const isCartReady = useEmbeddedCartReady();

  useEffect(() => {
    if (isCartReady && !getCart()?.state?.getPersist().currency) {
      getCart()?.state?.updatePersist({
        currency: getCurrencyFromBrowserLocale(typeof navigator === "undefined" ? undefined : navigator.language),
      });
    }
  }, [isCartReady]);
}

// productCode is the real, raw product code (e.g. "bouncieobd") — the catalog is the
// source of truth now, not checkoutdwa's PRODUCT_CODES constant (vestigial outside
// OBD-specific logic). Currency defaults to the browser locale unless overridden.
export function useEmbeddedCartCatalog(productCode: string, currencyOverride?: string) {
  const isCartReady = useEmbeddedCartReady();
  const [catalog, setCatalog] = useState(() => getCart()?.pricingCatalog);

  useEffect(() => {
    if (!isCartReady) return;

    const unsubscribe = getCart()?.events?.pricingLoaded?.subscribe(() => {
      setCatalog(getCart()?.pricingCatalog);
    });

    return unsubscribe;
  }, [isCartReady]);

  const currency =
    currencyOverride ?? getCurrencyFromBrowserLocale(typeof navigator === "undefined" ? undefined : navigator.language);

  // `products` isn't per-currency anymore — pricing is one level down, in each
  // product's own `variants` (one entry per currency). Only `subscriptions` is
  // still a flat per-currency array.
  const product = catalog?.products.find((p) => p.productCode === productCode);
  const variant = product?.variants?.find((v) => v.currency === currency);
  const subscription = catalog?.subscriptions.find((s) => s.currency === currency);

  return {
    currencyCode: variant?.currency?.toUpperCase(),
    currencySymbol: variant?.currencySymbol,
    unitPrice: variant?.unitPrice,
    // Variant attributes (e.g. Connector Type: J1772/NACS) a shopper picks between before
    // adding to cart — undefined for single-variant products (e.g. bouncieobd).
    attributes: product?.attributes,
    subscriptionUnitPrice: subscription?.baseUnitPrice,
    subscriptionAdjustedUnitPrice: subscription?.adjustedUnitPrice,
    subscriptionDiscountThreshold: subscription?.discountThreshold,
  };
}

// A pure open/close toggle — never adds to the cart. For a cart icon, where the intent
// is "show me my cart," not "add something." A button whose intent is adding a
// product should use useAddToEmbeddedCart instead.
export function useToggleEmbeddedCart() {
  return useCallback(() => {
    const state = getCart()?.state;
    if (!state) return;

    const { isCartPanelOpen } = state.getPersist();
    state.updatePersist({ isCartPanelOpen: !isCartPanelOpen });
  }, []);
}

// For any button whose *intent* is adding a specific product ("Add to Cart," "Buy
// Now," etc.), as opposed to useToggleEmbeddedCart's plain toggle. `selection`
// defaults to {}; together with productCode it's a cart line's identity.
export function useAddToEmbeddedCart(productCode: string, selection: ProductSelection = {}) {
  return useCallback(() => {
    const state = getCart()?.state;
    if (!state) return;

    // Validate against the catalog (no separate code registry anymore). Only refuse
    // once the catalog has loaded and definitively doesn't recognize this code —
    // proceed optimistically if it just hasn't loaded yet.
    const catalogProducts = getCart()?.pricingCatalog?.products;
    if (catalogProducts && !catalogProducts.some((p) => p.productCode === productCode)) {
      // A malformed/unknown productCode would persist a line item checkoutdwa's
      // price-preview can't resolve, which breaks pricing for the *entire* cart, not
      // just this line. Fail loud in dev, no-op in the cart, rather than corrupt it.
      console.error(`[useAddToEmbeddedCart] "${productCode}" isn't in the catalog — refusing to add it to the cart.`);
      return;
    }

    const { products } = state.getPersist();
    const quantity = state.utils.getProductQuantity(productCode, selection, products);
    if (!quantity) {
      state.updatePersist({ products: state.utils.setProductQuantity(productCode, selection, 1, products) });
    }
    state.updatePersist({ isCartPanelOpen: true });
  }, [productCode, selection]);
}

// Pushes an explicit currency into the cookie shared with checkoutdwa. checkoutdwa
// derives its legacy `locale` field from `currency` on its own, so no translation
// is needed — our "usd"/"cad" values already match its convention.
export function pushCurrencyToEmbeddedCart(currency: SupportedCurrency): void {
  getCart()?.state?.updatePersist({ currency });
}

// Pushes an explicit language into the shared cookie. Only sets `language`, not
// `locale` (a region/currency proxy, unrelated to display language) — lowercased
// to match checkoutdwa's convention (e.g. "en-us").
export function pushLanguageToEmbeddedCart(locale: SupportedLocale): void {
  getCart()?.state?.updatePersist({ language: locale.toLowerCase() });
}
